// frontend/admin-portal/src/layouts/AdminLayout.jsx
import React from "react";
import { useNavigate, Outlet } from "react-router-dom";
import {
  Layout,
  Sidebar,
  TopBar,
  Button,
  Badge,
  HomeIcon,
  BoxIcon,
  CartIcon,
  UsersIcon,
  CogIcon,
  TagIcon,
  LayersIcon,
  TruckIcon,
  WalletIcon,
  CoinsIcon,
  ShieldIcon,
  UsersCogIcon,
  ClipboardIcon,
  ReceiptIcon,
  TruckDeliveryIcon,
} from "@fiberhome/shared-ui";
import { useAuth } from "../contexts/AuthContext";
import UserMenu from "../components/navigation/UserMenu";
import { usePermission } from "../hooks/usePermission";
import { ProductsProvider, useProducts } from "../contexts/ProductsContext";

// Outer wrapper — just provides the context
export default function AdminLayout() {
  return (
    <ProductsProvider>
      <AdminLayoutInner />
    </ProductsProvider>
  );
}

// Inner component — same as before, but now can call useProducts()
function AdminLayoutInner() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { has, permissions } = usePermission();
  const { count: productsCount } = useProducts();

  if (process.env.NODE_ENV === "development") {
    // eslint-disable-next-line no-console
    console.log(
      "[AdminLayout] permissions:",
      permissions,
      "| user:",
      user?.email,
    );
  }

  const sections = [
    {
      label: "Main",
      items: [
        {
          to: "/",
          label: "Dashboard",
          icon: <HomeIcon />,
          end: true,
          permission: "dashboard.read",
        },
      ],
    },
    {
      label: "Store",
      items: [
        {
          to: "/products",
          label: "Products",
          icon: <BoxIcon />,
          permission: "products.read",
          badge: productsCount > 0 ? productsCount : undefined,
        },
        {
          to: "/categories",
          label: "Categories",
          icon: <LayersIcon />,
          permission: "categories.read",
        },
        {
          to: "/brands",
          label: "Brands",
          icon: <TagIcon />,
          permission: "brands.read",
        },
        {
          to: "/suppliers",
          label: "Suppliers",
          icon: <TruckIcon />,
          permission: "suppliers.read",
        },
      ],
    },
    {
      label: "Treasury",
      items: [
        {
          to: "/treasury",
          label: "Cash Flow",
          icon: <WalletIcon />,
          permission: "treasury.read",
        },
        {
          to: "/treasury/accounts",
          label: "Accounts",
          icon: <CoinsIcon />,
          permission: "treasury.read",
        },
        {
          to: "/treasury/babylon",
          label: "Babylon",
          icon: <ClipboardIcon />,
          permission: "treasury.babylon",
        },
      ],
    },
    {
      label: "Sales",
      items: [
        {
          to: "/orders",
          label: "Offers & Orders",
          icon: <CartIcon />,
          permission: "orders.read",
        },
        {
          to: "/customers",
          label: "Customers",
          icon: <UsersIcon />,
          permission: "customers.read",
        },
        {
          to: "/invoices",
          label: "Invoices",
          icon: <ReceiptIcon />,
          permission: "invoices.read",
        },
      ],
    },
    {
      label: "Procurement",
      items: [
        {
          to: "/requisitions",
          label: "Requisitions",
          icon: <ClipboardIcon />,
          permission: "procurement.read",
        },
        {
          to: "/purchase-orders",
          label: "Purchase Orders",
          icon: <TruckDeliveryIcon />,
          permission: "purchase_orders.read",
        },
        {
          to: "/goods-receipts",
          label: "Goods Receipts",
          icon: <ReceiptIcon />,
          permission: "goods_receipts.read",
        },
      ],
    },
    {
      label: "System",
      items: [
        {
          to: "/users",
          label: "Users",
          icon: <UsersCogIcon />,
          permission: "users.read",
        },
        {
          to: "/roles",
          label: "Roles",
          icon: <ShieldIcon />,
          permission: "roles.read",
        },
        {
          to: "/settings",
          label: "Settings",
          icon: <CogIcon />,
          permission: "settings.read",
        },
      ],
    },
  ];

  // Filter items by permission
  const visibleSections = sections
    .map((s) => ({
      ...s,
      items: s.items.filter((it) => !it.permission || has(it.permission)),
    }))
    .filter((s) => s.items.length > 0);

  const handleSidebarLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <Layout
      sidebar={({ collapsed, mobileOpen, closeMobile }) => (
        <Sidebar
          brand="FiberHome"
          brandMark="FH"
          sections={visibleSections}
          collapsed={collapsed}
          open={mobileOpen}
          onNavigate={closeMobile}
          footer={
            <Button
              variant="ghost"
              size="sm"
              block
              onClick={handleSidebarLogout}
            >
              Logout
            </Button>
          }
        />
      )}
      topbar={({ onToggleSidebar }) => (
        <TopBar
          title="Admin Portal"
          onToggleSidebar={onToggleSidebar}
          user={user}
          actions={
            <Badge variant="success" dot>
              Online
            </Badge>
          }
          userMenu={<UserMenu />}
        />
      )}
    >
      <Outlet />
    </Layout>
  );
}
